import React, { useEffect, useState } from 'react';
import { Button, Chip } from '@mui/material';
import { Add } from '@mui/icons-material';
import { formatDate } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { JournalEntry } from '../../interfaces/accounting.interface';
import { JournalEntryService } from '../../services/journal-entry.service';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

const getStatusColor = (status: string): 'warning' | 'success' | 'error' | 'default' => {
    switch (status) {
        case 'DRAFT': return 'warning';
        case 'POSTED': return 'success';
        case 'VOID': return 'error';
        default: return 'default';
    }
};

const JournalEntryList: React.FC = () => {
    const navigate = useNavigate();
    const [entries, setEntries] = useState<JournalEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const fetchEntries = async (pageNum: number) => {
        try {
            setLoading(true);
            const response = await JournalEntryService.getEntriesByInstitution(pageNum, 10);
            setEntries(response.content || []);
            setTotalPages(response.totalPages || 0);
            setPage(response.number || 0);
            setError('');
        } catch (err) {
            setError('Failed to load journal entries');
            console.error('Error fetching journal entries:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEntries(0);
    }, []);

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Journal Entries</h2>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => navigate('/accounting/journal-entries/create')}
                >
                    Create Journal Entry
                </Button>
            </div>

            {error && <div style={{ color: 'red', marginBottom: '16px' }}>{error}</div>}

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th style={tableHeaderStyle}>Entry Number</th>
                        <th style={tableHeaderStyle}>Date</th>
                        <th style={tableHeaderStyle}>Description</th>
                        <th style={tableHeaderStyle}>Status</th>
                        <th style={tableHeaderStyle}>Total Debit</th>
                        <th style={tableHeaderStyle}>Total Credit</th>
                    </tr>
                </thead>
                <tbody>
                    {entries.length === 0 ? (
                        <tr>
                            <td style={tableCellStyle} colSpan={6}>No journal entries found</td>
                        </tr>
                    ) : (
                        entries.map(entry => (
                            <tr
                                key={entry.id}
                                style={{ cursor: 'pointer' }}
                                onClick={() => navigate(`/accounting/journal-entries/${entry.id}`)}
                            >
                                <td style={tableCellStyle}>{entry.entryNumber || '-'}</td>
                                <td style={tableCellStyle}>{entry.date ? formatDate(entry.date) : '-'}</td>
                                <td style={tableCellStyle}>{entry.description || '-'}</td>
                                <td style={tableCellStyle}>
                                    <Chip label={entry.status} color={getStatusColor(entry.status)} size="small" />
                                </td>
                                <td style={tableCellStyle}>{entry.totalDebit?.toFixed(2) || '0.00'}</td>
                                <td style={tableCellStyle}>{entry.totalCredit?.toFixed(2) || '0.00'}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            {/* Pagination */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '20px' }}>
                <Button
                    variant="outlined"
                    disabled={page === 0}
                    onClick={() => fetchEntries(page - 1)}
                >
                    Previous
                </Button>
                <span>Page {page + 1} of {totalPages || 1}</span>
                <Button
                    variant="outlined"
                    disabled={page >= totalPages - 1}
                    onClick={() => fetchEntries(page + 1)}
                >
                    Next
                </Button>
            </div>
        </div>
    );
};

export default JournalEntryList;
